import React from "react";
import {
    StyleProp,
    TextStyle,
    View,
    ViewStyle,
    StyleSheet,
} from "react-native";
import { useTranslation } from "locales";
import { useThemedStyle, useTheme, StyleFunction } from "../../theme";
import { IconButton } from "../icon-button";

type DateTimeType = "date" | "time" | "dateTime";

interface NavigationArrowsProps {
    dateTimeType?: DateTimeType;
    date: Date | undefined;
    maxDate?: Date;
    disabled?: boolean;
    onPrevious: (date: Date) => void;
    onNext: (date: Date) => void;
    style?: StyleProp<ViewStyle | TextStyle>;
    testID?: string;
}

export function NavigationArrows({
    dateTimeType = "date",
    date,
    maxDate,
    disabled,
    onPrevious,
    onNext,
    style,
    testID,
}: NavigationArrowsProps) {
    const styles = useThemedStyle(styleFunc);
    const { colors: themeColors } = useTheme();
    const [t] = useTranslation();

    const isTimeType = dateTimeType === "time";

    const canGoNext = React.useMemo(() => {
        if (!date) return false;
        if (maxDate && date >= maxDate) return false;
        return true;
    }, [date, maxDate]);

    const canGoPrevious = React.useMemo(() => {
        return date !== undefined;
    }, [date]);

    const handlePrevious = React.useCallback(() => {
        if (!date || !canGoPrevious) return;

        let newDate: Date;
        if (isTimeType) {
            newDate = new Date(date.getTime() - 15 * 60 * 1000);
        } else {
            newDate = new Date(date);
            newDate.setDate(newDate.getDate() - 1);
        }
        onPrevious(newDate);
    }, [date, canGoPrevious, isTimeType, onPrevious]);

    const handleNext = React.useCallback(() => {
        if (!date || !canGoNext) return;

        let newDate: Date;
        if (isTimeType) {
            newDate = new Date(date.getTime() + 15 * 60 * 1000);
        } else {
            newDate = new Date(date);
            newDate.setDate(newDate.getDate() + 1);
        }
        onNext(newDate);
    }, [date, canGoNext, isTimeType, onNext]);

    const previousLabel = isTimeType
        ? t("datetime.previousHour", "Previous hour")
        : t("datetime.previousDay", "Previous day");
    const nextLabel = isTimeType
        ? t("datetime.nextHour", "Next hour")
        : t("datetime.nextDay", "Next day");

    return (
        <View
            style={[styles.container, disabled ? styles.disabled : null, style]}
            testID={testID}
        >
            <IconButton
                name="caret-left"
                onPress={handlePrevious}
                disabled={disabled || !canGoPrevious}
                tooltip={previousLabel}
                color={themeColors.secondary}
                style={[styles.arrow, canGoPrevious ? styles.active : null]}
            />
            <IconButton
                name="caret-right"
                onPress={handleNext}
                disabled={disabled || !canGoNext}
                tooltip={nextLabel}
                color={themeColors.secondary}
                style={[styles.arrow, canGoNext ? styles.active : null]}
            />
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 4,
        backgroundColor: "white",
        borderWidth: StyleSheet.hairlineWidth,
        borderColor: theme.colors.secondary,
        borderStyle: "solid",

        borderRadius: theme.borderRadiusSmall,
        borderLeftWidth: 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        marginBottom: theme.spacingScale,
    },

    active: {
        color: theme.colors.secondary,
    },

    disabled: {
        backgroundColor: theme.colors.grey50,
        borderColor: theme.colors.grey500,
    },

    arrow: {},
});
